import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react'
import { Alert, Form, Skeleton, Spin } from 'antd'
import { VStack, HStack } from '../../../../../ui/Stack'
import { DefaultJsonForm } from '../../../../../components/v2/default-json-form'
import { isEqual, cloneDeep } from 'lodash'
import { useSaveResult } from '../../hooks/useSaveResult'
import { EmployeeFormData } from '../../types/types'
import { prepareEmployeeSavePayload } from '../../utils/prepare-employee-save-payload'

const COMPLIANCE_FIELD_NAMES = new Set([
  'compliance_medical_insurance',
  'compliance_patent',
  'compliance_receipt',
])

interface EmployeeFormsProps {
  id: string | number
  formData: EmployeeFormData | null
  loading: boolean
  error: any
  onDirtyChange?: (isDirty: boolean) => void
}

export interface EmployeeFormsRef {
  save: () => Promise<void>
}

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
}

export const EmployeeForms = memo(
  forwardRef<EmployeeFormsRef, EmployeeFormsProps>((props: EmployeeFormsProps, ref) => {
    const { id, formData, loading, error, onDirtyChange } = props
    const [form] = Form.useForm()
    const { saveResult } = useSaveResult()
    const formRef = useRef<any>(null)
    const initialFormDataRef = useRef<any>(null)
    const [localFormData, setLocalFormData] = useState<any>(null)
    const [isSaving, setIsSaving] = useState(false)

    const updateDirtyState = useCallback(
      (currentFormData: any) => {
        const isDirty = !isEqual(initialFormDataRef.current, currentFormData)
        onDirtyChange?.(isDirty)
      },
      [onDirtyChange],
    )

    useEffect(() => {
      if (formData?.schema) {
        const initialFormData = formData.formData || {}
        initialFormDataRef.current = cloneDeep(initialFormData)
        formRef.current = initialFormData
        setLocalFormData(initialFormData)
        form.setFieldsValue(initialFormData)
        onDirtyChange?.(false)
      }
    }, [formData, form, onDirtyChange])

    const saveEmployee = useCallback(async () => {
      if (!formRef.current) {
        return
      }
      const payload = prepareEmployeeSavePayload(
        formRef.current,
        formData?.schema || null,
        formData?.permissions,
      )
      const idString = id === 'new' ? 'new' : String(id)
      setIsSaving(true)
      try {
        const ok = await saveResult(idString, payload, formData?.schema || null, id === 'new')
        if (!ok) {
          throw new Error('Save failed')
        }
      } finally {
        setIsSaving(false)
      }
    }, [id, saveResult, formData])

    // Экспортируем метод save через ref
    useImperativeHandle(ref, () => ({ save: saveEmployee }), [saveEmployee])

    const onFormDataChange = useCallback(
      async ({ formData: newFormData }) => {
        if (!formRef.current) {
          return
        }
        setLocalFormData(newFormData)
        formRef.current = { ...newFormData }
        updateDirtyState(formRef.current)
      },
      [updateDirtyState],
    )

    const schema = useMemo(() => {
      const raw = formData?.schema || { type: 'object', properties: {}, required: [] }
      if (!raw.properties) return raw
      const s = JSON.parse(JSON.stringify(raw))

      // В режиме редактирования не показываем поля, которые нельзя редактировать (permissions: view)
      const permissions = formData?.permissions || {}
      const editableFieldNames = Object.keys(s.properties).filter(
        name => permissions?.[name] !== 'view' || COMPLIANCE_FIELD_NAMES.has(name),
      )

      s.properties = editableFieldNames.reduce((acc, name) => {
        acc[name] = s.properties[name]
        return acc
      }, {} as Record<string, any>)

      if (Array.isArray(s.required)) {
        s.required = s.required.filter((name: string) => editableFieldNames.includes(name))
      }

      if (s.groups) {
        Object.keys(s.groups).forEach(groupId => {
          const group = s.groups[groupId]
          if (!group?.fields) return
          group.fields = group.fields.filter((name: string) => editableFieldNames.includes(name))
          if (!group.fields.length) {
            delete s.groups[groupId]
          }
        })
      }

      // В режиме редактирования phones — массив с add/remove, каждый элемент — InputPhone
      if (s.properties?.phones) {
        s.properties.phones = {
          title: 'Номера телефонов',
          type: 'array',
          items: { type: 'string', format: 'phone' },
        }
      }
      return s
    }, [formData?.schema, formData?.permissions])

    if (error && !loading && !formData) {
      return (
        <HStack gap={'8'} className={'mapWrapper'}>
          При выполнении запроса возникла ошибка. Попробуйте перезагрузить страницу или открыть
          карточку повторно.
        </HStack>
      )
    }

    // Проверяем, что схема не пустая
    if (!schema || !schema.properties || Object.keys(schema.properties).length === 0) {
      return (
        <HStack gap={'8'} className={'mapWrapper'}>
          Нет доступных полей для отображения. Проверьте права доступа.
        </HStack>
      )
    }

    return (
      <HStack gap={'8'} max>
        <Form
          {...formItemLayout}
          layout="horizontal"
          form={form}
          scrollToFirstError={true}
          style={{ width: '100%' }}
        >
          {error && !loading ? (
            <div className={'alertWrapper'}>
              <Alert
                message="При выполнении операции возникла ошибка:"
                showIcon
                type="error"
                description={error}
              />
            </div>
          ) : null}
          {!loading ? (
            <VStack gap={'16'} max align={'stretch'} className={'employeeForm'}>
              <Spin spinning={isSaving}>
                {formData &&
                  schema &&
                  schema.properties &&
                  Object.keys(schema.properties).length > 0 && (
                    <DefaultJsonForm
                      formData={localFormData || {}}
                      schema={schema}
                      onChange={onFormDataChange}
                      orientation="horizontal"
                    />
                  )}
              </Spin>
            </VStack>
          ) : (
            <Skeleton active />
          )}
        </Form>
      </HStack>
    )
  }),
)

EmployeeForms.displayName = 'EmployeeForms'
